import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { type FC, useState } from 'react';
import { useValidateLicensePlateMutation } from 'services/api/insuranceQuoteForm.api';
import type { InsuranceQuoteFormData } from 'services/types/insuranceQuoteForm';
import { LIGHT_GREY, NAVY, RADIUS_CARD, RADIUS_COIN, RADIUS_ITEM, SURFACE_TINT, YELLOW } from 'styles/tokens';

interface LicensePlateStepProps {
  formData: InsuranceQuoteFormData;
  onFormDataChange: (updates: Partial<InsuranceQuoteFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  setIsLoading: (loading: boolean) => void;
}

const LicensePlateStep: FC<LicensePlateStepProps> = ({ formData, onFormDataChange, onNext, onBack, setIsLoading }) => {
  const [validateLicensePlate] = useValidateLicensePlateMutation();
  const [plateInput, setPlateInput] = useState(formData.licensePlate);
  const [error, setError] = useState<string | null>(null);
  const [validated, setValidated] = useState(!!formData.vehicleInfo);

  const handleValidate = async () => {
    if (!plateInput.trim()) {
      setError('Por favor ingresa una patente');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const result = await validateLicensePlate({ licensePlate: plateInput }).unwrap();

      if (!result.isValid) {
        setError(result.error || 'Patente inválida');
        setValidated(false);
        return;
      }

      onFormDataChange({
        licensePlate: plateInput,
        formattedPlate: result.formattedPlate || plateInput.toUpperCase(),
        vehicleInfo: result.vehicleInfo || null,
      });

      setValidated(true);
    } catch (_err) {
      setError('Error al validar la patente. Por favor intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    if (validated) {
      onNext();
    }
  };

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlateInput(e.target.value.toUpperCase());
    setValidated(false);
    setError(null);
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: RADIUS_CARD,
          p: { xs: 2.5, md: 3 },
          mb: 3,
          bgcolor: SURFACE_TINT,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <DirectionsCarIcon sx={{ color: NAVY, fontSize: '1.3rem' }} />
          <Typography variant="subtitle1" sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, color: NAVY }}>
            Datos del vehículo
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(21, 24, 58, 0.6)', mb: 2.5, fontFamily: 'Jost, sans-serif' }}>
          La patente se usará para obtener información del vehículo. (Solo Usados)
        </Typography>

        {/* ── Input row: input + button + result chip ── */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
          <TextField
            label="Patente"
            placeholder="Ej: ABCD12"
            value={plateInput}
            onChange={handlePlateChange}
            error={!!error}
            helperText={error}
            size="small"
            sx={{
              flex: '1 1 200px',
              maxWidth: { sm: 280 },
              '& .MuiOutlinedInput-root': {
                borderRadius: RADIUS_ITEM,
                fontFamily: 'Jost, sans-serif',
                bgcolor: '#ffffff',
              },
              '& .MuiInputLabel-root': { fontFamily: 'Jost, sans-serif' },
              '& .MuiFormHelperText-root': { fontFamily: 'Jost, sans-serif' },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleValidate();
              }
            }}
            slotProps={{
              htmlInput: { style: { textTransform: 'uppercase' } },
            }}
          />
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              handleValidate();
            }}
            disabled={!plateInput.trim()}
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              bgcolor: NAVY,
              color: '#ffffff',
              borderRadius: RADIUS_COIN,
              px: 3,
              height: 40,
              whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#0d0f28' },
              '&.Mui-disabled': { bgcolor: 'rgba(21, 24, 58, 0.08)', color: 'rgba(21, 24, 58, 0.38)' },
            }}
          >
            Buscar
          </Button>

          {validated && formData.vehicleInfo && (
            <Fade in>
              <Chip
                icon={<CheckCircleOutlinedIcon />}
                label={`${formData.vehicleInfo.brandName} ${formData.vehicleInfo.modelName} ${formData.vehicleInfo.year || ''}`}
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  height: 40,
                  borderRadius: RADIUS_COIN,
                  bgcolor: 'rgba(37, 97, 111, 0.08)',
                  color: '#25616F',
                  boxShadow: '0 2px 8px rgba(37, 97, 111, 0.12)',
                  '& .MuiChip-icon': { color: '#25616F' },
                }}
              />
            </Fade>
          )}
          {validated && !formData.vehicleInfo && (
            <Fade in>
              <Chip
                icon={<CheckCircleOutlinedIcon />}
                label="Patente válida"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  height: 40,
                  borderRadius: RADIUS_COIN,
                  bgcolor: 'rgba(44, 88, 137, 0.08)',
                  color: '#2C5889',
                  boxShadow: '0 2px 8px rgba(44, 88, 137, 0.12)',
                  '& .MuiChip-icon': { color: '#2C5889' },
                }}
              />
            </Fade>
          )}
        </Box>
      </Box>

      {validated && !formData.vehicleInfo && (
        <Fade in>
          <Alert severity="info" sx={{ mb: 3, borderRadius: RADIUS_ITEM, fontFamily: 'Jost, sans-serif' }}>
            No se encontró información del vehículo en Autored. Puedes continuar de todas formas.
          </Alert>
        </Fade>
      )}

      <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disableElevation
          onClick={onBack}
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            bgcolor: LIGHT_GREY,
            color: NAVY,
            borderRadius: RADIUS_COIN,
            px: 3,
            '&:hover': { bgcolor: '#d4d4d4' },
          }}
        >
          Atrás
        </Button>

        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!validated}
          disableElevation
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            bgcolor: YELLOW,
            color: NAVY,
            borderRadius: RADIUS_COIN,
            px: 4,
            '&:hover': { bgcolor: '#e5c009' },
            '&.Mui-disabled': { bgcolor: 'rgba(21, 24, 58, 0.08)', color: 'rgba(21, 24, 58, 0.38)' },
          }}
        >
          Continuar
        </Button>
      </Box>
    </Box>
  );
};

export default LicensePlateStep;
